<template>
  <div
    id="self-service-menu-drawer"
    :class="[
      'flex flex-col justify-stretch transition-all bg-racq-true-white w-[85%] lg:w-[304px] max-w-[356px] md:max-w-lg h-screen-dynamic lg:h-screen max-h-screen-dynamic lg:min-h-screen lg:max-h-none border-r border-light-grey z-70 top-0 absolute lg:sticky overflow-y-scroll lg:overflow-y-auto lg:flex-shrink-0 scrollbar-thin',
      menuClasses,
      {
        'scrollbar-racq': !scrollbarIsHidden,
        'scrollbar-transparent': scrollbarIsHidden,
      },
    ]"
    @scroll="handleScroll"
    @scrollend="handleScrollEnd"
  >
    <div class="flex justify-between p-4">
      <a :href="homeLink?.value.href ?? '/'" class="lg:hidden">
        <img src="@/assets/icons/racq-icon-sm.svg" height="20" width="48" class="h-[20px]" alt="racq-icon" />
      </a>
      <a :href="homeLink?.value.href ?? '/'" class="hidden lg:block">
        <img src="@/assets/icons/racq-icon-lg.svg" height="22" width="56" class="h-[22px]" alt="racq-icon" />
      </a>
      <img
        src="@/assets/icons/close-icon.svg"
        height="24"
        width="24"
        alt="close-icon"
        class="block lg:hidden"
        @click="toggleMenu"
      />
    </div>
    <div class="grow flex flex-col justify-between px-4 pb-10 md:px-6 md:pt-6 md:pb-0 md:mt-2">
      <div>
        <!-- Avatar -->
        <a
          :href="profileLink?.value.href ?? '#'"
          class="group flex flex-col gap-4 lg:gap-3 no-underline text-center w-full mt-1 lg:mt-6"
        >
          <span
            class="block w-14 h-14 text-center rounded-2xl border border-racq-blue-border group-hover:border-racq-blue-active bg-racq-anti-flash-white mx-auto"
          >
            <span class="text-xl2 tracking-[-0.48px] !leading-[56px] py-[1px] px-2">
              {{
                title
                  .split(' ')
                  .map((n) => n.charAt(0))
                  .join('')
              }}
            </span>
            <img v-show="false" src="#" :alt="`${title}`" class="w-14 h-14" />
          </span>
          <span class="text-xl-bold lg:text-base-bold text-racq-dark-grey group-hover:text-racq-blue-active">
            {{ title }}
          </span>
        </a>

        <div class="text-base lg:text-m text-racq-grey text-center mb-6 lg:mb-5">{{ subtitleLabel }}</div>

        <button
          class="btn btn-md lg:btn-sm btn-primary w-full"
          :data-test-id="adobeTargetId(secondaryButton, { suffix: 'sidebar' })"
          @click="props.onSecondaryClick?.()"
        >
          {{ secondaryButton }}
        </button>

        <!-- Primary Menu Items -->
        <div class="my-8 md:mb-6 flex flex-col gap-1" data-test-id="primary-menu-items">
          <Link
            v-for="(item, index) in primaryMenuItems"
            :key="index"
            :field="item.fields.link"
            :data-test-id="adobeTargetId(item.fields?.link?.value?.text, { suffix: 'sidebar' })"
            class="flex gap-2 p-2 w-full hover:bg-racq-anti-flash-white active:bg-racq-white active:no-underline transition no-underline rounded-lg focus:rounded-lg"
            :class="{
              'bg-racq-neutral-grey': index == activeMenuItem,
            }"
          >
            <Image :media="(item.fields.icon as Field<ImageFieldValue>)?.value" :editable="true" />
            <p v-if="!isEditorActive()" class="text-lg-bold lg:text-m-bold text-racq-dark-grey pt-1">
              {{ item.fields.link?.value.text }}
            </p>
          </Link>
        </div>
        <hr class="border-racq-white my-8" />

        <!-- Secondary Menu Items -->
        <div class="my-8 md:my-6 px-2 flex flex-col gap-3 md:gap-4">
          <div v-for="(item, index) in secondaryMenuItems" :key="index" class="flex gap-2">
            <Link
              :field="item.fields.link"
              class="text-m-bold text-racq-grey no-underline hover:!underline hover:!decoration-[10%]"
              :data-test-id="adobeTargetId(item.fields?.link?.value?.text, { suffix: 'sidebar' })"
            />
          </div>
        </div>

        <hr class="border-racq-white mt-6 md:hidden" />
      </div>
      <div class="sticky md:pb-6 md:bottom-0 bg-racq-true-white">
        <!-- Logout Button -->
        <button class="btn btn-sm btn-secondary w-full mt-8 sticky bottom-0" @click="logout">
          {{ logoutButton }}
          <img src="@/assets/icons/logout-icon.svg" alt="logout-icon" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Item, Field } from '@sitecore-jss/sitecore-jss/types/layout';
import { Image, isEditorActive, Link, Text } from '@sitecore-jss/sitecore-jss-vue';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import { ref, computed, onMounted } from 'vue';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { useAccountStore } from '@/store';
import { format } from 'date-fns';

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<{
  menuClasses: string;
  homeLink?: LinkField;
  profileLink: LinkField;
  title: string;
  subtitle: string;
  primaryButton: string;
  secondaryButton: string;
  logoutButton: string;
  toggleMenu: () => void;
  primaryMenuItems: Item[];
  secondaryMenuItems: Item[];
  onSecondaryClick?: () => void;
}>();

const SCROLLBAR_HIDE_DELAY = 500;

// Get primary menu item index
let activeMenuItem = props.primaryMenuItems.findIndex(
  (item) => (item.fields?.link as LinkField)?.value?.href?.toLowerCase() == window.location.pathname.toLowerCase()
);
// Store to browser session for product details page
if (activeMenuItem != -1) {
  sessionStorage.activeMenuItem = activeMenuItem;
} else {
  activeMenuItem = sessionStorage.activeMenuItem ?? 0;
}

const accountStore = useAccountStore();
const scrollbarIsHidden = ref<boolean>(false);

const title = computed(() => (isEditorActive() ? 'Alex Smith' : props.title));

const subtitleLabel = computed(() => {
  if (!accountStore.identityDetails?.membershipJoined) return '';
  return props.subtitle.replace('{{joinYear}}', format(accountStore.identityDetails?.membershipJoined, 'yyyy'));
});

onMounted(() => {
  setTimeout(() => {
    scrollbarIsHidden.value = true;
  }, SCROLLBAR_HIDE_DELAY);
});

const handleScroll = () => {
  scrollbarIsHidden.value = false;
};

const handleScrollEnd = () => {
  setTimeout(() => {
    scrollbarIsHidden.value = true;
  }, SCROLLBAR_HIDE_DELAY);
};

const logout = () => {
  accountStore.logout();
};

/**
 * Format adobe target data test id
 * @param label
 * @param options
 * @returns
 */
const adobeTargetId = (
  label?: string,
  options?: {
    prefix?: string;
    suffix?: string;
  }
) => {
  return [options?.prefix, ...(label?.toLowerCase()?.split(' ') ?? []), options?.suffix].filter((o) => o).join('_');
};
</script>
